/*
  <announcement-bar> — behaviour for sections/announcement-bar.liquid. The section only loads
  this file when it has more than one message for small screens, or an offer code.

  Below 990px the bar shows one message at a time (the section marks the first one active,
  so nothing waits for this script). The script adds:
    - previous / next arrows; using them stops rotation for good;
    - optional rotation (data-autoplay, data-speed in ms). It waits while the pointer is over
      the bar, while focus is inside it, while the tab is hidden, and in the theme editor, and
      never starts for visitors who prefer reduced motion;
    - inactive messages are made inert, so keyboard and screen reader users only meet the
      visible one;
    - offer codes copy to the clipboard on click, with a spoken "copied" confirmation.
  From 990px every message is visible side by side and none of this runs.

  Theme editor: selecting a message block shows it and holds rotation until deselected.
*/
const smallScreen = window.matchMedia('(max-width: 989px)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-ann-item]'));
    this.prevButton = this.querySelector('[data-ann-prev]');
    this.nextButton = this.querySelector('[data-ann-next]');
    this.status = this.querySelector('[data-ann-status]');

    const designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.autoplay = this.hasAttribute('data-autoplay') && !designMode && !reducedMotion.matches;
    this.speed = Math.max(3000, Number(this.dataset.speed) || 5000);
    this.holds = new Set();

    this.abort = new AbortController();
    const { signal } = this.abort;

    this.prevButton?.addEventListener('click', () => this.step(-1, true), { signal });
    this.nextButton?.addEventListener('click', () => this.step(1, true), { signal });
    this.addEventListener('click', (event) => this.copyCode(event), { signal });

    const hold = (reason, on) => {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
    };
    this.addEventListener('pointerenter', () => hold('pointer', true), { signal });
    this.addEventListener('pointerleave', () => hold('pointer', false), { signal });
    this.addEventListener('focusin', () => hold('focus', true), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) hold('focus', false);
    }, { signal });
    document.addEventListener('visibilitychange', () => hold('hidden', document.hidden), { signal });

    this.addEventListener('shopify:block:select', (event) => {
      hold('editor', true);
      const item = this.items.find((entry) => entry.contains(event.target) || event.target.contains(entry));
      if (item && this.rotation.includes(item)) this.show(item);
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => hold('editor', false), { signal });

    smallScreen.addEventListener('change', () => this.setup(), { signal });
    this.setup();
  }

  disconnectedCallback() {
    this.abort?.abort();
    clearInterval(this.timer);
  }

  // Messages that take part in the one-at-a-time view (not "desktop only").
  get rotation() {
    return this.items.filter((item) => !item.classList.contains('ann-item--desktop'));
  }

  setup() {
    clearInterval(this.timer);
    const items = this.rotation;
    const single = smallScreen.matches;
    const several = single && items.length > 1;

    if (this.prevButton) this.prevButton.hidden = !several;
    if (this.nextButton) this.nextButton.hidden = !several;

    if (!single) {
      this.items.forEach((item) => { item.inert = false; });
      return;
    }

    const active = items.find((item) => item.classList.contains('is-active')) || items[0];
    if (active) this.show(active);
    if (several && this.autoplay) {
      this.timer = setInterval(() => {
        if (this.holds.size === 0) this.step(1);
      }, this.speed);
    }
  }

  show(target) {
    this.rotation.forEach((item) => {
      const on = item === target;
      item.classList.toggle('is-active', on);
      item.inert = !on;
    });
  }

  step(direction, byVisitor = false) {
    const items = this.rotation;
    if (items.length < 2) return;
    if (byVisitor) {
      this.autoplay = false;
      clearInterval(this.timer);
    }
    const current = items.findIndex((item) => item.classList.contains('is-active'));
    const next = (current + direction + items.length) % items.length;
    this.show(items[next]);
  }

  copyCode(event) {
    const button = event.target.closest('[data-ann-copy]');
    if (!button || !navigator.clipboard) return;
    const code = button.dataset.annCopy;
    navigator.clipboard.writeText(code).then(() => {
      button.textContent = 'Copied!';
      if (this.status) this.status.textContent = `Code ${code} copied`;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => {
        button.textContent = code;
        if (this.status) this.status.textContent = '';
      }, 2000);
    }).catch(() => {});
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
